import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { axiosInstance } from "../../../services/axiosInstance";
import { endpoints } from "../../../services/endpoint";
import PermissionTable from "../editSecurityprofile/PermissionTable";

import "./editProfile.css";
import {
  ArrowDropUp as ArrowDropUpIcon,
  AccountCircle,
  ArrowDropDown as ArrowDropDownIcon,

  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
const EditSecurityProfilePage = ({ profile, onClose, onUpdate }) => {
  const [profileName, setProfileName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description);
  
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [loading, setLoading] = useState(false);
    const [actionClicked, setActionClicked] = useState(null); 
  // ✅ Convert array of permissions to object format (same as Add page)
  const initialPermissions = useMemo(() => {
    const permObj = {};
    (profile.permissions || []).forEach((perm) => {
      permObj[perm] = true;
    });
    return permObj;
  }, [profile.permissions]);

  // ✅ Use same state format as Add page (object, not array)
  const [permissions, setPermissions] = useState(initialPermissions);

  // ✅ Use same toggle function as Add page
  const togglePermission = (key) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ✅ Use same permission formatting functions as Add page
  const normalizePermission = (perm) => {
    // Convert dashes into dot + PascalCase
    return perm.replace(/-([a-z])/gi, (_, c) => "." + c.toUpperCase());
  };

  const formatPermissionsForAPI = (permObj) => {
    return Object.entries(permObj)
      .filter(([_, value]) => value) // only include checked/true permissions
      .map(([key]) => normalizePermission(key));
  };
    const [cancelButtonLoading, setCancelButtonLoading] = useState(false);
  const handleCancel = () => {
     setActionClicked("cancel");
    setCancelButtonLoading(true);
    setTimeout(() => {
      setCancelButtonLoading(false);
      setActionClicked(null);
      onClose();
    }, 300);
  };
  const handleUpdate = async () => {
    const regex = /^[A-Za-z0-9 _@-]{1,127}$/;

    if (!regex.test(profileName)) {
      setError(true);
      setHelperText(
        "Invalid name: Only A-Z, 0-9, spaces, @, _ and - allowed. Max 127 characters."
      );
      return;
    }

    // ✅ Format permissions the same way as Add page
    const selectedPermissions = formatPermissionsForAPI(permissions);

    console.log("🔄 Formatted permissions for API:", selectedPermissions);

    const payload = {
      action: "editsecurityprofile",
      ID: localStorage.getItem("EncryptedId"),
      awsAccount: localStorage.getItem("CustomerAWSAccountId"),
      region: localStorage.getItem("Region"),
      InstanceAlias: localStorage.getItem("instanceName"),
      securityProfileId: profile.securityProfileId,
      securityProfileName: profileName,
      securityProfileDescription: description,
      permissions: selectedPermissions // ✅ use formatted permissions array
    };

    try {
      setLoading(true);
      const res = await axiosInstance.post(
        endpoints.displaysecurityprofile.securityprofile,
        payload
      );

      const parsed = JSON.parse(res.data?.body || "{}");
      const updated = {
        ...profile,
        name: profileName,
        description,
        permissions: selectedPermissions, // ✅ update with formatted permissions
        tags: parsed.Tags || profile.tags,
        lastModifiedTime: parsed.LastModifiedTime || new Date().toISOString()
      };

      onUpdate(updated);
    } catch (err) {
      console.error("Failed to update profile:", err);
      
      // Enhanced error handling
      if (err.response?.status === 400) {
        try {
          const errorBody = JSON.parse(err.response.data?.body || "{}");
          if (errorBody.invalidPermissions) {
            setHelperText(`Invalid permissions: ${errorBody.invalidPermissions.join(", ")}`);
            console.error("❌ Invalid permissions:", errorBody.invalidPermissions);
          } else {
            setHelperText(errorBody.error || "Update failed");
          }
        } catch (parseError) {
          setHelperText("Update failed");
        }
      } else {
        setHelperText("Update failed");
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Use same permission sections as Add page
 const permissionSections = [
    {
      id: 'routing',
      title: 'Routing',
      description: 'These permissions are for configuring access to different agent routing capabilities.',
      items: [
        { id: 'RoutingPolicies', label: 'Routing profiles' },
        { id: 'HoursOfOperation', label: 'Hours of operation' },
        { id: 'Queues', label: 'Queues' },
        { id: 'TaskTemplates', label: 'Task templates' },
        { id: 'PredefinedAttributes', label: 'Predefined attributes' }
      ],
      hasEnableDisable: true,
    },
    {
      id: 'security',
      title: 'Security Profiles',
      description: 'These permissions are for managing security profiles.',
      items: [
        { id: 'SecurityProfiles', label: 'Security Profiles' },
      ],
      hasEnableDisable: false,
    },
    {
      id: 'users',
      title: 'User Management',
      description: 'These permissions are for managing users and agent hierarchy.',
      items: [
        { id: 'AgentGrouping', label: 'Agent hierarchy' },
      ],
      hasEnableDisable: true,
    },
    {
      id: 'agent-states',
      title: 'Agent States',
      description: 'These permissions are for managing agent states.',
      items: [
        { id: 'AgentStates', label: 'Agent States' },
      ],
      hasEnableDisable: true,
    },
    {
      id: 'contact-flows',
      title: 'Contact Flows & Channels',
      description: 'Contact flow and channel management permissions.',
      items: [
        { id: 'ContactFlows', label: 'Contact Flows' },
        { id: 'ContactFlowModules', label: 'Contact Flow Modules' },
        { id: 'Prompts', label: 'Prompts' },
        { id: 'Bots', label: 'Bots' },
        { id: 'PhoneNumbers', label: 'Phone Numbers' },
        { id: 'EmailAddresses', label: 'Email Addresses' }
      ],
      hasEnableDisable: false,
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      description: 'Analytics, metrics, and reporting permissions.',
      items: [
        { id: 'AccessMetrics', label: 'Access Metrics' },
        { id: 'ContactSearch', label: 'Contact Search' },
        { id: 'MetricsReports', label: 'Metrics Reports' },
        { id: 'ReportsAdmin', label: 'Reports Admin' },
        { id: 'Rules', label: 'Rules' },
        { id: 'RulesGenerativeAI', label: 'Rules Generative AI' }
      ],
      hasEnableDisable: false,
    }
  ];

  return (
     <Container className="addhour-content-container">
    <Box display="flex" flexDirection="column" alignItems="flex-start" mb={2} gap={1}>
        <IconButton
          onClick={onClose}
          size="medium"
          sx={{
            border: "1px solid #ccc",       // border color
            borderRadius: "50%",            // makes it circular
            padding: 1,                     // inner spacing
            bgcolor: "#f5f5f5",             // background color
            "&:hover": { bgcolor: "#e0e0e0" } // hover effect
          }}
        >
          <ArrowBackIcon />
        </IconButton>
       {/* Title */}
       <Typography variant="h4" className="phone-text">
         Edit Security Profile {profile.name}
       </Typography>
     </Box>
    <Box className="quick-form-section">
    
      <Box className="form-section">
        <Typography>Edit Security Profile</Typography>

        <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }} width="100%">
          <TextField
            fullWidth
            label="Name"
           
            required
            value={profileName}
            onChange={(e) => {
              setProfileName(e.target.value);
              setError(false);
              setHelperText("");
            }}
            error={error}
            helperText={error ? helperText : ""}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
         
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
        </Box>
      </Box>

      {/* Permissions Section */}
      <Box sx={{ mt: 4 }}>
        {permissionSections.map((section) => (
          <Accordion key={section.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography>{section.title}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {section.description}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <PermissionTable
                items={section.items}
                hasEnableDisable={section.hasEnableDisable}
                permissions={permissions}          // ✅ object format like Add page
                togglePermission={togglePermission} // ✅ same toggle function as Add page
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <div className="buttons-addhour">
       <Button
                onClick={handleCancel}
               disabled={actionClicked === "save" || cancelButtonLoading}
            >
              {actionClicked === "cancel" ? <CircularProgress size={18} /> : "Cancel"}
              </Button>
             
        <Button variant="contained" onClick={handleUpdate} 
         disabled={actionClicked === "cancel" || loading}
               >
                 {actionClicked === "save" ? (
                   <CircularProgress size={18} sx={{ color: "white" }} />
                 ) : (
                   "Save"
                 )}
                 </Button>
      </div>
    </Box>
    </Container>
  );
};

export default EditSecurityProfilePage;